import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags as useRootFeatureFlags } from 'providers/FeatureFlagsProvider';
import { SECONDARY_API_CLIENT } from 'services/API';
import {
  formatPhoneNumbers,
  PHONE_FORMAT_MODES,
  PHONE_ROW_PREFIX
} from 'UI/components/organisms/PhoneNumbersForm/utils';
import { ContactEntity, ContactRole, HiringAuthorityEntity } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { UIStatus } from 'UI/constants/status';
import { getId } from 'UI/utils';

import { CANDIDATE_DRAFTS_PROVIDERS } from './NewCandidate.constants';

/**
 * @returns {UseFeatureFlagsReturnType}
 */
export const useFeatureFlags = () => {
  const { checkIfFeatureFlagEnabled } = useRootFeatureFlags();

  return {
    canIncludeContactDataSheet: checkIfFeatureFlagEnabled(
      FeatureFlags.NewCandidateIncludesContactDataSheet
    ),
    canSaveAsDraft: checkIfFeatureFlagEnabled(FeatureFlags.Drafts)
  };
};

const formatAttachments = attachments => {
  if (!attachments) return [];
  return attachments.map(item => ({ ...item, fileType: { id: item?.file_type_id } }));
};

const transformPhoneData = phonesArray => {
  const result = {
    phones: [],
    phoneDefaultId: null
  };

  if (!phonesArray) return result;

  phonesArray.forEach((phoneData, index) => {
    const phoneId = getId();

    result.phones.push({
      id: phoneId,
      countryCode: {
        id: phoneData?.countryCode?.id,
        country_iso_2: phoneData?.countryCode?.country_iso_2,
        country_iso_3: phoneData?.countryCode?.country_iso_3,
        country: phoneData?.countryCode?.country,
        code: phoneData?.countryCode?.code
      },
      phone: '',
      ext: '',
      use: '',
      type: ''
    });

    if (index === 0) {
      result.phoneDefaultId = phoneId;
    }

    result[`${PHONE_ROW_PREFIX.CountryCode}${phoneId}`] = {
      id: phoneData?.countryCode?.id,
      country_iso_2: phoneData?.countryCode?.country_iso_2,
      country_iso_3: phoneData?.countryCode?.country_iso_3,
      country: phoneData?.countryCode?.country,
      code: phoneData?.countryCode?.code
    };

    result[`${PHONE_ROW_PREFIX.Phone}${phoneId}`] = phoneData?.phone?.toString() || '';
    result[`${PHONE_ROW_PREFIX.Ext}${phoneId}`] = '';

    result[`${PHONE_ROW_PREFIX.Use}${phoneId}`] = {
      id: phoneData?.use?.id,
      title: phoneData?.use?.title
    };

    result[`${PHONE_ROW_PREFIX.Type}${phoneId}`] = {
      id: phoneData?.type?.id,
      title: phoneData?.type?.title
    };
  });

  return result;
};

const buildFromContact = ({ contactId, hiringId }) => {
  if (!contactId && !hiringId) return null;
  return hiringId
    ? { id: hiringId, type: HiringAuthorityEntity.id, role_id: ContactRole.HiringAuthority }
    : { id: contactId, type: ContactEntity.id, role_id: ContactRole.Name };
};

const formatApplicantData = (data, checkIfFeatureFlagEnabled) => {
  const {
    city,
    companyId,
    companyName,
    contactId,
    dataSheetId,
    email,
    firstName,
    hiringId,
    keywords,
    lastName,
    nickname,
    otherEmail,
    phones,
    position,
    source,
    sourceUrl,
    specialty,
    state,
    subspecialty,
    title,
    zipCode
  } = data || {};

  return {
    city_id: city,
    company: { id: companyId, name: companyName },
    contactId,
    dataSheet: { id: dataSheetId, progress: {} },
    email,
    firstName,
    fromContact: buildFromContact({ contactId, hiringId }),
    functionalTitle: position,
    hiringId,
    keywordTags: checkIfFeatureFlagEnabled(FeatureFlags.CandidatesKeywordsFromApplicantUrl)
      ? formatKeywords(keywords)
      : [],
    lastName,
    linkedinUrl: sourceUrl,
    nickname,
    personalEmail: otherEmail,
    source,
    specialty,
    state_id: state,
    subspecialty,
    title,
    zip: zipCode,
    ...transformPhoneData(phones)
  };
};

const formatKeywords = keywords => {
  if (!keywords || keywords?.length === 0) {
    return [];
  }
  return keywords.map(keyword => {
    return {
      keyword,
      display_text: keyword
    };
  });
};

const createEndpoint = (type, id) => {
  const provider = CANDIDATE_DRAFTS_PROVIDERS[type];
  return provider && provider.apiEndpoint.replace(':id', id);
};

export const useNewCandidateFromUrl = ({ type, id }) => {
  const history = useHistory();
  const source = useRef(type);

  const endpoint = createEndpoint(type, id);
  const { state, setState } = useFetchWithStatusV2({
    endpoint,
    apiInstance: SECONDARY_API_CLIENT,
    initialStatus: endpoint ? UIStatus.Loading : UIStatus.Default
  });

  const { checkIfFeatureFlagEnabled } = useRootFeatureFlags();

  const formData = formatApplicantData(state.results, checkIfFeatureFlagEnabled);

  useEffect(() => {
    if (!id || !type) return;

    if (
      (id && state.status === UIStatus.Success) ||
      (state.status === UIStatus.Error &&
        state.responseStatusCode !== HTTPStatusCodes.InternalServerError)
    ) {
      source.current = type;
    }
  }, [id, type, history, state.status, state.responseStatusCode]);

  const applicantFormData =
    state.status === UIStatus.Success
      ? {
          ...formData,
          phones: formatPhoneNumbers(formData, PHONE_FORMAT_MODES.preload),
          dataSheetId: state.results?.dataSheetId,
          attachments: formatAttachments(state.results?.attachments),
          applicantId: type === CANDIDATE_DRAFTS_PROVIDERS.talentPool.id ? id : null
        }
      : null;

  const resetExternalSource = () => {
    source.current = null;
  };

  return {
    state,
    applicantFormData,
    setState,
    externalSource: source.current,
    resetExternalSource
  };
};
